import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field, Th, Td } from "./Customers";
import { Plus, MessageSquare, AlertTriangle, Clock, Check, X, Send } from "lucide-react";

const STATUS = {
  new: { cls: "border-[#0033A0] bg-[#0033A0]/10 text-[#0033A0]", label: "New" },
  open: { cls: "border-[#0033A0] bg-[#0033A0]/10 text-[#0033A0]", label: "Open" },
  in_progress: { cls: "border-[#F5B700] bg-[#F5B700]/10 text-[#0A0A0A]", label: "In progress" },
  waiting: { cls: "border-[#495057] bg-[#495057]/10 text-[#495057]", label: "Waiting" },
  resolved: { cls: "border-[#0B7A75] bg-[#0B7A75]/10 text-[#0B7A75]", label: "Resolved" },
  closed: { cls: "border-[#0A0A0A] bg-[#F8F9FA] text-[#0A0A0A]", label: "Closed" },
  cancelled: { cls: "border-[#D90429] bg-[#D90429]/10 text-[#D90429]", label: "Cancelled" },
};
const FALLBACK_STATUS = { cls: "border-[#495057] bg-[#F8F9FA] text-[#495057]", label: "Unknown" };
const statusFor = (s) => STATUS[s] || FALLBACK_STATUS;
const PRIO = {
  low: "#495057", normal: "#0033A0", high: "#F5B700", critical: "#D90429",
};

export default function CRM() {
  const [cases, setCases] = useState([]);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/crm/cases").then(({ data }) => setCases(data));
  useEffect(() => { load(); }, []);
  const filtered = filter === "all" ? cases : cases.filter((c) => c.status === filter);
  const counts = cases.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});
  const overdueCount = cases.filter((c) => c.overdue).length;

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="crm-page">
      <PageHeader
        title="CRM · Cases"
        subtitle="Customer support tickets with SLA tracking. Sign-off on resolve. Customer 360 from sales + ledger."
        action={
          <button onClick={() => setOpen(true)} data-testid="new-case-btn"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm rounded-sm">
            <Plus size={14} /> New case
          </button>
        }
      />

      {overdueCount > 0 && (
        <div className="mt-6 border border-[#D90429] bg-[#D90429]/5 p-4 flex items-center gap-3" data-testid="overdue-banner">
          <AlertTriangle size={16} className="text-[#D90429]" />
          <span className="text-sm font-medium"><strong>{overdueCount}</strong> case{overdueCount > 1 ? "s are" : " is"} past SLA — review now.</span>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {["all", "new", "in_progress", "waiting", "resolved", "closed"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`crm-filter-${s}`}
            className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border ${filter === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] text-[#495057]"}`}>
            {s.replace("_", " ")} {counts[s] ? <span className="ml-1 opacity-60">({counts[s]})</span> : null}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-[#0A0A0A] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>Number</Th><Th>Subject</Th><Th>Customer</Th><Th>Priority</Th><Th>Status</Th><Th>Due</Th></tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => (
              <tr key={c.id} data-testid={`case-row-${i}`} onClick={() => setView(c)}
                className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA] cursor-pointer">
                <Td className="font-mono text-xs font-semibold">{c.number}</Td>
                <Td><div className="font-medium">{c.subject || c.title || "—"}</div></Td>
                <Td className="text-[#495057]">{c.customer_name}</Td>
                <Td><span className="text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5" style={{ borderColor: PRIO[c.priority] || "#495057", color: PRIO[c.priority] || "#495057" }}>{c.priority}</span></Td>
                <Td><span data-testid={`case-status-${c.status}`} className={`inline-block text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${statusFor(c.status).cls}`}>{statusFor(c.status).label}</span></Td>
                <Td className="text-xs">
                  {c.due_at && (
                    <span className={c.overdue ? "text-[#D90429] font-semibold" : "text-[#495057]"}>
                      {c.overdue && <AlertTriangle size={11} className="inline mr-1" />}
                      {new Date(c.due_at).toLocaleString()}
                    </span>
                  )}
                </Td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No cases.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && <NewCase onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {view && <CaseDetail caseId={view.id} onClose={() => setView(null)} onChanged={() => { setView(null); load(); }} />}
    </div>
  );
}

function NewCase({ onClose, onSaved }) {
  const [custs, setCusts] = useState([]);
  const [f, setF] = useState({ customer_id: "", channel: "email", subject: "", description: "", priority: "normal", sla_hours: 24 });
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/customers").then(({ data }) => setCusts(data)); }, []);
  const save = async (e) => { e.preventDefault();
    try { await api.post("/crm/cases", { ...f, sla_hours: parseInt(f.sla_hours) }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="New case" onClose={onClose} testid="new-case-modal" wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="new-case-form">
        <label className="col-span-2 block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Customer</span>
          <select required value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} data-testid="case-customer"
            className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— select —</option>
            {custs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Channel</span>
          <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid="case-channel"
            className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            {["email", "phone", "whatsapp", "walk_in", "web", "other"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Priority</span>
          <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="case-priority"
            className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            {Object.keys(PRIO).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </label>
        <div className="col-span-2"><Field label="Subject" required value={f.subject} onChange={(v) => setF({ ...f, subject: v })} testid="case-subject" /></div>
        <div className="col-span-2">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Description</span>
            <textarea required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} data-testid="case-description"
              className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm focus:border-[#0A0A0A] focus:outline-none" />
          </label>
        </div>
        <Field label="SLA hours" type="number" value={f.sla_hours} onChange={(v) => setF({ ...f, sla_hours: v })} testid="case-sla" />
        {err && <div className="col-span-2 text-sm text-[#D90429]">{err}</div>}
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="case-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Open case</button>
        </div>
      </form>
    </Modal>
  );
}

function CaseDetail({ caseId, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [reply, setReply] = useState("");
  const [err, setErr] = useState("");
  const [resolution, setResolution] = useState("");
  const refresh = async () => { const { data } = await api.get(`/crm/cases/${caseId}`); setC(data); };
  useEffect(() => {
    let alive = true;
    api.get(`/crm/cases/${caseId}`).then(({ data }) => { if (alive) setC(data); });
    return () => { alive = false; };
  }, [caseId]);
  if (!c) return null;

  const setStatus = async (status, extra = {}) => {
    try { await api.post(`/crm/cases/${c.id}/status`, { status, ...extra }); await refresh(); onChanged?.(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/crm/cases/${c.id}/comments`, { body: reply, kind: "reply" }); setReply(""); await refresh(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal title={`${c.number} · ${c.subject || c.title || ""}`} onClose={onClose} testid="case-detail-modal" wide>
      <div className="space-y-5" data-testid="case-detail">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${statusFor(c.status).cls}`}>{statusFor(c.status).label}</span>
          <span className="text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5" style={{ borderColor: PRIO[c.priority] || "#495057", color: PRIO[c.priority] || "#495057" }}>{c.priority}</span>
          <span className="text-xs text-[#495057]"><Clock size={11} className="inline mr-1" /> SLA {c.sla_hours || "—"}h · due {c.due_at ? new Date(c.due_at).toLocaleString() : "—"}</span>
          <span className="text-xs text-[#495057]">· {c.customer_name}</span>
        </div>

        <div className="border border-[#DEE2E6] bg-[#F8F9FA] p-4 text-sm">
          <div className="text-xs uppercase tracking-[0.15em] text-[#495057] mb-1">Description ({c.channel})</div>
          <div className="whitespace-pre-wrap">{c.description || (c.activity && c.activity[0]?.text) || "—"}</div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#495057] mb-3 inline-flex items-center gap-2"><MessageSquare size={12} /> Activity ({(c.comments?.length || 0) + (c.activity?.length || 0)})</div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(c.comments || []).map((cm) => (
              <div key={cm.id} data-testid={`comment-${cm.id}`} className={`border-l-2 pl-3 py-2 ${cm.kind === "status_change" ? "border-[#495057] bg-[#F8F9FA]" : cm.kind === "reply" ? "border-[#0033A0]" : "border-[#FF4F00]"}`}>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">{cm.user_name} · {cm.kind} · {new Date(cm.created_at).toLocaleString()}</div>
                <div className="text-sm whitespace-pre-wrap mt-1">{cm.body}</div>
              </div>
            ))}
            {(c.activity || []).map((a) => (
              <div key={a.id} className="border-l-2 pl-3 py-2 border-[#25D366]">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">{a.from} · via {a.via} · {new Date(a.at).toLocaleString()}</div>
                <div className="text-sm whitespace-pre-wrap mt-1">{a.text}</div>
              </div>
            ))}
          </div>
        </div>

        {c.status !== "closed" && c.status !== "cancelled" && (
          <div className="flex gap-2">
            <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply or add a note…"
              data-testid="case-reply-input"
              className="flex-1 bg-white border border-[#DEE2E6] px-3 py-2 text-sm focus:border-[#0A0A0A] focus:outline-none" />
            <button onClick={sendReply} data-testid="case-reply-send" className="px-3 bg-[#0033A0] text-white rounded-sm"><Send size={14} /></button>
          </div>
        )}

        {err && <div className="text-sm text-[#D90429]">{err}</div>}

        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-[#DEE2E6]">
          {c.status === "new" && (
            <button onClick={() => setStatus("in_progress")} data-testid="case-start-btn" className="px-4 py-2 text-sm bg-[#F5B700] text-[#0A0A0A]">Take it (Start)</button>
          )}
          {c.status === "in_progress" && (
            <>
              <button onClick={() => setStatus("waiting")} data-testid="case-wait-btn" className="px-4 py-2 text-sm border border-[#495057] text-[#495057]">Waiting on customer</button>
              <input value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Resolution summary"
                data-testid="case-resolution-input"
                className="bg-white border border-[#DEE2E6] px-3 py-2 text-sm w-64" />
              <button onClick={() => setStatus("resolved", { resolution })} disabled={!resolution.trim()} data-testid="case-resolve-btn"
                className="px-4 py-2 text-sm bg-[#0B7A75] disabled:opacity-50 text-white inline-flex items-center gap-1.5"><Check size={14} /> Resolve</button>
            </>
          )}
          {c.status === "waiting" && (
            <button onClick={() => setStatus("in_progress")} data-testid="case-resume-btn" className="px-4 py-2 text-sm bg-[#F5B700] text-[#0A0A0A]">Resume</button>
          )}
          {c.status === "resolved" && (
            <button onClick={() => setStatus("closed")} data-testid="case-close-btn" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Close case</button>
          )}
          {!["closed", "cancelled"].includes(c.status) && (
            <button onClick={() => setStatus("cancelled")} data-testid="case-cancel-btn" className="px-4 py-2 text-sm border border-[#D90429] text-[#D90429] inline-flex items-center gap-1.5"><X size={14} /> Cancel</button>
          )}
        </div>
      </div>
    </Modal>
  );
}
